import Cookies from "js-cookie";
import { isArray } from "lodash";
import Router from "next/router";
import { createContext, type Dispatch, FC, SetStateAction, useState } from "react";
import { pushAlert } from "redux/actions/alertActions";
import store from "redux/store";
import { SWRConfig } from "swr";

type User = any;

export const TempAuthContext = createContext<{
    setToken: Dispatch<SetStateAction<string | null>>;
    token: string | null;
    setUser: Dispatch<SetStateAction<User | null>>;
    user: User | null;
}>({
    setToken: () => {},
    token: null,
    setUser: () => {},
    user: null,
});

const fetcher =
    (token: string | null) =>
    (key: string | [string, object], init: RequestInit, throwErrors = false) => {
        let resource = key as string,
            params = {};
        if (isArray(key)) {
            resource = key?.[0];
            params = key?.[1];
        }

        const url = new URL(
            resource.startsWith("http") || resource.startsWith("api/")
                ? resource
                : process.env.NEXT_PUBLIC_API_HOST + resource
        );

        const stringify = (value: any) => {
            switch (typeof value) {
                case "object":
                    return JSON.stringify(value);
                default:
                    return String(value);
            }
        };

        Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach((v, i) => url.searchParams.append(`${key}[${i}]`, stringify(v)));
            } else url.searchParams.append(key, stringify(value));
        });

        // a terminal login wins; otherwise fall back to the ambient session, matching
        // what the DataTable fetcher in components/DataTable/helpers.ts already does
        const authToken = token || Cookies.get("token");

        return fetch(url.toString(), {
            headers: {
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                ["Content-Type"]: "application/json",
                ["X-Requested-With"]: "XMLHttpRequest",
                ["Content-Language"]: Router.locale || "de",
            },
            ...init,
        }).then(async (res) => {
            if (res.status === 503) {
                Router.replace("/maintenance");
                return { data: [] };
            }

            const data = await res.json();
            // only show alert if not checking permission
            if (res.status === 403 && !resource.match(/clubs\/(.*)\/check/gm))
                store.dispatch(pushAlert(data?.message, "danger"));
            if (throwErrors && res.status === 500) throw new Error(data);
            return data;
        });
    };

const TempAuthProvider: FC<React.PropsWithChildren<React.PropsWithChildren<unknown>>> = ({ children }) => {
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<User | null>(null);

    return (
        <TempAuthContext.Provider value={{ token, setToken, user, setUser }}>
            <SWRConfig value={{ fetcher: fetcher(token) }}>{children}</SWRConfig>
        </TempAuthContext.Provider>
    );
};

export default TempAuthProvider;
